import { IconWithTooltip } from "@/app/components/iconWithTooltip";
import { showToast } from "@/app/slices/statusSlice";
import { useIsUserRole } from "@/app/utils/auth";
import {
  ArrowRightCircleIcon,
  ArrowRightStartOnRectangleIcon,
  BookOpenIcon,
  ChatBubbleBottomCenterIcon,
  LinkIcon,
  LockClosedIcon,
  PencilSquareIcon,
  QuestionMarkCircleIcon,
  ShareIcon,
  SparklesIcon,
  UsersIcon,
} from "@heroicons/react/16/solid";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router";
import ExpandableText from "../../components/ExpandableText";
import FileUploadArea from "../../components/FileUploadArea";
import Header from "../../components/header/Header";
import Modal, { openModal } from "../../components/modal";
import { useToastEffect } from "../../hooks";
import {
  useGetCourseQuery,
  useLeaveCourseMutation,
} from "../../services/courseApi";
import { buildQueryString } from "../../utils/api.util";
import {
  getLecturerDisplayName,
  isFetchBaseQueryError,
} from "../../utils/utils";
import CourseActionCard from "./CourseActionCard";
import CourseSetupProgress from "./CourseSetupTracker";
import MaterialSection from "./MaterialSection";

function CoursePage() {
  const navigate = useNavigate();
  const { t } = useTranslation(["coursePage", "toast", "header", "commons"]);
  const dispatch = useDispatch();

  const { courseId } = useParams();

  useEffect(() => {
    if (!courseId) {
      navigate("/");
    }
  }, [courseId, navigate]);

  if (!courseId) {
    return null;
  }

  const [isManaging, setIsManaging] = useState(false);
  const isAdmin = useIsUserRole("admin");

  const fileUploadRef = useRef<HTMLDivElement | null>(null);

  const {
    data: course,
    isError: courseIsError,
    error: courseError,
  } = useGetCourseQuery(courseId);

  useEffect(() => {
    if (courseIsError && isFetchBaseQueryError(courseError)) {
      if (courseError.status === 403) {
        navigate("enroll");
      }
    }
  }, [courseIsError, courseError, navigate]);

  const [
    leaveCourse,
    { isError: leaveIsError, isSuccess: leaveSuccess, error: leaveError },
  ] = useLeaveCourseMutation();

  useEffect(() => {
    setIsManaging(course?.isManaging || isAdmin);
  }, [course?.isManaging, isAdmin]);

  useToastEffect({
    isError: leaveIsError,
    isSuccess: leaveSuccess,
    error: leaveError,
    successMessage: t("coursePage.successLeave", {
      courseName: course?.name,
      ns: "toast",
    }),
  });

  const editCourse = () => {
    const path = buildQueryString("manage", {
      edit: true,
    });
    navigate(path);
  };

  const handleLeaveCourse = async () => {
    await leaveCourse(courseId);
    await new Promise((resolve) => setTimeout(resolve, 20));
    navigate("/");
  };

  const handleShareCourse = () => {
    navigator.clipboard.writeText(
      `${window.location.origin}/course/${courseId}`,
    );
    dispatch(
      showToast({
        message: t("coursePage.shareCopied", { ns: "toast" }),
        type: "info",
      }),
    );
  };

  return (
    <Header
      breadcrumbs={[
        {
          name:
            course?.name ?? t("breadcrumbFallback.course", { ns: "header" }),
        },
      ]}
      backButtonRoute="/"
    >
      {course && (
        <>
          <div className="flex flex-col justify-between gap-4 md:flex-row">
            <div className="flex flex-col justify-between">
              <div>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <div className="font-bold text-2xl">{course.name}</div>
                  <div className="flex items-center gap-2">
                    {isManaging && (
                      <>
                        <IconWithTooltip
                          icon={<PencilSquareIcon className="h-6 w-6" />}
                          tooltip={t("tooltipEdit")}
                          handler={() => {
                            editCourse();
                          }}
                          className="btn btn-ghost btn-circle btn-sm"
                        />
                        {course?.isPasswordProtected && (
                          <IconWithTooltip
                            icon={<LockClosedIcon className="h-6 w-6" />}
                            tooltip={t("tooltipPassword")}
                            className="btn-sm"
                          />
                        )}
                      </>
                    )}
                    <IconWithTooltip
                      icon={<ShareIcon className="h-6 w-6" />}
                      tooltip={t("tooltipShare")}
                      handler={() => handleShareCourse()}
                      className="btn btn-ghost btn-circle btn-sm"
                    />
                    {course?.isEnrolled && (
                      <IconWithTooltip
                        icon={
                          <ArrowRightStartOnRectangleIcon className="h-6 w-6" />
                        }
                        tooltip={t("tooltipLeaveCourse")}
                        handler={() => {
                          openModal("leave_modal");
                        }}
                        className="btn btn-ghost btn-circle btn-sm"
                      />
                    )}
                  </div>
                </div>
                <div className="mt-2 mb-4 text-lg">
                  {getLecturerDisplayName(course.lecturer)}
                </div>
                <div className="ml-md flex flex-col space-y-1">
                  <div className="flex flex-row items-start text-md">
                    <div
                      className="tooltip tooltip-right"
                      data-tip={t("tooltipCourseDescription")}
                    >
                      <span className="inline-block">
                        <BookOpenIcon className="mr-2 h-6 w-6" />
                      </span>
                    </div>
                    <ExpandableText text={course.description} maxLines={3} />
                  </div>

                  {isManaging && (
                    <>
                      <div className="flex flex-row items-center">
                        <div
                          className="tooltip tooltip-right"
                          data-tip={t("tooltipLLM")}
                        >
                          <SparklesIcon className="h-6 w-6" />
                        </div>
                        <div className="badge badge-primary badge-md ml-sm whitespace-nowrap">
                          <p className="font-bold">{course.modelType}</p>
                        </div>
                      </div>

                      <div className="flex flex-row items-center text-lg">
                        <div
                          className="tooltip tooltip-right"
                          data-tip={t("tooltipParticipants")}
                        >
                          <UsersIcon className="mr-2 h-6 w-6" />
                        </div>
                        {course.enrolledUsers}
                      </div>
                    </>
                  )}

                  {course.extLink && (
                    <div className="flex flex-row items-center">
                      <LinkIcon className="mr-2 h-6 w-6" />
                      <a
                        href={course.extLink}
                        target="_blank"
                        rel="noreferrer"
                        className="link text-md"
                      >
                        {t("externalCourseLink")}
                      </a>
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-4 flex flex-row flex-wrap gap-4">
                <CourseActionCard
                  title={t("chatCard.title")}
                  cardText={t("chatCard.text")}
                  buttonText={t("chatCard.button")}
                  icon={
                    <ChatBubbleBottomCenterIcon className="ml-sm h-7 w-7" />
                  }
                  onClick={() => navigate("chat")}
                />
                <CourseActionCard
                  title={t("quizCard.title")}
                  cardText={t("quizCard.text")}
                  buttonText={t("quizCard.button")}
                  icon={<QuestionMarkCircleIcon className="ml-sm h-7 w-7" />}
                  onClick={() => navigate("quiz")}
                />
              </div>
            </div>
            {isManaging && (
              <div className="border-0">
                <CourseSetupProgress
                  uploadedMaterials={course.uploadedMaterials}
                  createdQuestions={course.createdQuestions}
                  fileUploadRef={fileUploadRef}
                />
              </div>
            )}
          </div>
          {isManaging && (
            <>
              <div className="divider" />
              <h1 className="mb-md font-bold text-2xl">
                {t("analyticsSectionTitle")}
              </h1>
              <div>
                <button
                  type="button"
                  className="btn btn-primary mr-md flex w-full justify-between shadow-sm hover:shadow-lg sm:w-64"
                  onClick={() => navigate("manage/dashboard")}
                >
                  <span className="font-semibold text-md sm:text-lg">
                    {t("viewDashboard")}
                  </span>
                  <ArrowRightCircleIcon className="ml-sm h-7 w-7" />
                </button>
              </div>
              <div className="mt-lg">
                <h1 className="mb-md font-bold text-2xl">
                  {t("manageQuizQuestions")}
                </h1>
                <div className="flex flex-col items-center gap-x-4 md:flex-row">
                  <button
                    type="button"
                    className="btn btn-accent w-full sm:w-48"
                    onClick={() => navigate("manage/quiz")}
                  >
                    <span className="font-semibold text-md">
                      {t("generateQuestions")}
                    </span>
                  </button>
                  <button
                    type="button"
                    className="btn btn-accent m-sm w-full sm:w-48"
                    onClick={() => {
                      const path = buildQueryString("manage/quiz", {
                        edit: true,
                      });
                      navigate(path);
                    }}
                  >
                    <span className="font-semibold text-md">
                      {t("editQuestions")}
                    </span>
                  </button>
                </div>
                <div className="divider" />
                <h1 className="mb-md font-bold text-2xl">
                  {t("manageResources")}
                </h1>
                <div
                  ref={fileUploadRef}
                  className="my-2 flex w-full flex-row items-center gap-x-4"
                >
                  <FileUploadArea acceptedFileFormat=".pdf,.txt,audio/*,video/*" />
                </div>
                <MaterialSection courseId={courseId} />
              </div>
            </>
          )}
        </>
      )}
      <Modal
        id="leave_modal"
        title={t("modalLeaveCourse")}
        content={t("modalLeaveCourseContent")}
        onConfirmLabel={t("modalLeaveCourseConfirmLabel")}
        onConfirm={handleLeaveCourse}
        onCancelLabel={t("cancel", { ns: "commons" })}
        onCancel={() => {}}
      />
    </Header>
  );
}

export default CoursePage;
